import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Seo, breadcrumbLd, ORIGIN } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CatalogFilterToolbar, CatalogFilterSidebar, useCatalogFilterParams } from "@/components/CatalogFilters";
import { useAuth } from "@/context/AuthContext";
import { categoryLabel, brandUrl, categoryUrl } from "@/lib/productUrl";
import { Loader2 } from "lucide-react";

const PAGE_SIZE = 48;

const SILO_CONFIG = {
  "prom-dresses":       { category: "dresses",     occasion: "prom",       context: "dresses" },
  "homecoming-dresses": { category: "dresses",     occasion: "homecoming", context: "dresses" },
  "graduation-dresses": { category: "dresses",     occasion: "graduation", context: "dresses" },
  "tuxedos":            { category: "tuxedos",     occasion: undefined,    context: "tuxedos" },
  "accessories":        { category: "accessories", occasion: undefined,    context: "accessories" },
  "shoes":              { category: "shoes",       occasion: undefined,    context: "shoes" },
};

/**
 * Category silo hub page — /prom-dresses, /homecoming-dresses, /graduation-dresses, /tuxedos, /accessories, /shoes.
 * Shows all designers in the category + a filterable grid backed by /api/products.
 */
export default function CategorySilo() {
  const location = useLocation();
  const categorySlug = location.pathname.replace(/^\//, "").split("/")[0];
  const cfg = SILO_CONFIG[categorySlug] || SILO_CONFIG["prom-dresses"];
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [favs, setFavs] = useState(new Set());
  const [filters] = useCatalogFilterParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [facets, setFacets] = useState(null);
  // Guards against multiple in-flight page loads and stale-batch application
  // when the filter changes mid-request. `fetchToken` bumps every time we
  // reset the grid; only the current token's responses are accepted.
  const fetchToken = useRef(0);
  const sentinelRef = useRef(null);

  // Silo metadata (label + designers) + facets — only fetch once per slug
  useEffect(() => {
    setData(null);
    setFacets(null);
    api.siloCategory(categorySlug).then(({ data }) => setData(data)).catch(() => setData({ notFound: true }));
    api.productFacets({ category: cfg.category, occasion: cfg.occasion }).then(({ data }) => setFacets(data)).catch(() => setFacets(null));
    if (user) api.favorites().then(({ data }) => setFavs(new Set(data.map((p) => p.id)))).catch(() => {});
  }, [categorySlug, user]);

  const fetchPage = useCallback(async (skip, token) => {
    const params = {
      category: cfg.category,
      occasion: cfg.occasion,
      color: filters.color.length ? filters.color.join(",") : undefined,
      silhouette: filters.silhouette.length ? filters.silhouette.join(",") : undefined,
      neckline: filters.neckline.length ? filters.neckline.join(",") : undefined,
      length: filters.length.length ? filters.length.join(",") : undefined,
      sleeve: filters.sleeve.length ? filters.sleeve.join(",") : undefined,
      size: filters.size.length ? filters.size.join(",") : undefined,
      designer: filters.designer.length ? filters.designer.join(",") : undefined,
      product_type: filters.product_type?.length ? filters.product_type.join(",") : undefined,
      price_min: filters.price_min,
      price_max: filters.price_max,
      sort: filters.sort,
      paginate: true,
      skip,
      limit: PAGE_SIZE,
    };
    const { data: resp } = await api.products(params);
    // Discard responses that arrived after a filter change reset the grid.
    if (token !== fetchToken.current) return null;
    const list = Array.isArray(resp) ? resp : (resp.items || resp.products || []);
    return {
      items: Array.isArray(list) ? list : [],
      total: resp.total ?? (Array.isArray(list) ? list.length : 0),
    };
  }, [
    cfg.category, cfg.occasion,
    // Serialize filters — array/object refs would trigger every render.
    JSON.stringify(filters), // eslint-disable-line react-hooks/exhaustive-deps
  ]);

  // Reset + load first page whenever the category or any filter changes
  useEffect(() => {
    if (!categorySlug) return;
    fetchToken.current += 1;
    const token = fetchToken.current;
    setProducts([]);
    setTotal(0);
    setLoading(true);
    fetchPage(0, token)
      .then((res) => {
        if (!res || token !== fetchToken.current) return;
        setProducts(res.items);
        setTotal(res.total);
      })
      .finally(() => {
        if (token === fetchToken.current) setLoading(false);
      });
  }, [categorySlug, fetchPage]);

  // Load the next page. Called by the intersection observer + as a safety
  // net button at the bottom of the grid.
  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return;
    if (products.length >= total) return;
    setLoadingMore(true);
    const token = fetchToken.current;
    try {
      const res = await fetchPage(products.length, token);
      if (res && token === fetchToken.current) {
        setProducts((prev) => {
          // Dedupe by id in case the backend returns overlapping rows.
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...res.items.filter((p) => !seen.has(p.id))];
        });
        setTotal(res.total);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, loading, loadingMore, products.length, total]);

  // Auto-load when the sentinel enters the viewport (~one screen from the
  // bottom). Uses IntersectionObserver so it's cheap on scroll.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { rootMargin: "800px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  if (!data) return <div className="h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-rosemetal" /></div>;
  if (data.notFound) return <div className="max-w-[900px] mx-auto px-5 pt-24 pb-32 text-center"><h1 className="font-display text-5xl">Not Found</h1><p className="font-body mt-4 text-muted-foreground">This category doesn't exist.</p></div>;

  const label = categoryLabel(categorySlug);
  const path = categoryUrl(categorySlug);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: label, path },
  ];
  const collectionPageLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${label} at Heather's Boutique`,
    url: `${ORIGIN}${path}`,
    description: `Shop ${data.total.toLocaleString()} ${label.toLowerCase()} at Heather's Boutique. Curated designer looks with dress-lock, virtual try-on, and Wisconsin-first exclusivity.`,
  };

  return (
    <div data-testid={`silo-${categorySlug}`} className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 pb-24">
      <Seo
        title={`${label} — Designer Formal`}
        description={`Shop ${data.total.toLocaleString()} ${label.toLowerCase()} — Amarra, Sherri Hill, Jovani, La Femme. Lock, try on & own the look.`}
        path={path}
        jsonLd={[breadcrumbLd(crumbs), collectionPageLd]}
      />
      <Breadcrumbs items={crumbs} />

      <header className="text-center py-10">
        <h1 className="font-display text-6xl sm:text-7xl leading-none">{label}</h1>
        <p className="font-body text-sm text-muted-foreground mt-4 max-w-xl mx-auto">
          {data.total.toLocaleString()} designer looks — hand-curated. Reserve, try on, and lock your look for your school.
        </p>
      </header>

      {/* Tux-family sub-nav — makes tuxedo accessories & shoes discoverable
          from the /tuxedos page (previously only surfaced in the footer). */}
      {["tuxedos", "accessories", "shoes"].includes(categorySlug) && (
        <nav
          data-testid="tux-family-subnav"
          className="mb-8 border-b border-cream pb-4 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
          aria-label="The tuxedo edit"
        >
          <span className="font-label text-[9px] tracking-[0.3em] text-ink/40 mr-2 hidden sm:inline">
            THE TUXEDO EDIT ·
          </span>
          {[
            { slug: "tuxedos", label: "Tuxedos & Suits" },
            { slug: "accessories", label: "Accessories" },
            { slug: "shoes", label: "Shoes" },
          ].map((t) => (
            <Link
              key={t.slug}
              to={`/${t.slug}`}
              data-testid={`tux-family-tab-${t.slug}`}
              className={`px-4 py-2 rounded-full font-label text-[10px] tracking-[0.25em] transition-colors ${
                categorySlug === t.slug
                  ? "bg-ink text-cream"
                  : "border border-cream text-ink/70 hover:border-ink hover:text-ink"
              }`}
            >
              {t.label.toUpperCase()}
            </Link>
          ))}
        </nav>
      )}

      {/* Filter toolbar (top) + grid with sticky left-column filter sidebar */}
      <div className="grid lg:grid-cols-[240px_1fr] gap-8 mt-4">
        <CatalogFilterSidebar designers={data.brands || []} context={cfg.context} availableFacets={facets} />

        <div>
          <CatalogFilterToolbar designers={data.brands || []} activeCount={total} context={cfg.context} availableFacets={facets} />

          {loading && products.length === 0 ? (
            <div className="h-64 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-rosemetal" /></div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display text-3xl mb-2">Nothing matches those filters.</p>
              <p className="font-body text-sm text-muted-foreground">Try clearing a couple to see more looks.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                {products.map((p, i) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    index={i}
                    isFav={favs.has(p.id)}
                    focusColor={filters.color.length === 1 ? filters.color[0] : null}
                  />
                ))}
              </div>

              {/* Infinite-scroll sentinel + status row */}
              <div ref={sentinelRef} className="h-1" aria-hidden />
              <div className="mt-10 mb-4 flex flex-col items-center gap-3">
                {loadingMore && (
                  <div data-testid="silo-loading-more" className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin text-rosemetal" />
                    <span className="font-label text-[10px] tracking-widest">LOADING MORE LOOKS…</span>
                  </div>
                )}
                {!loadingMore && products.length < total && (
                  <button
                    onClick={loadMore}
                    data-testid="silo-load-more-btn"
                    className="border border-ink text-ink px-8 py-3 font-label text-[10px] tracking-[0.28em] rounded-sm hover:bg-ink hover:text-cream transition-colors"
                  >
                    LOAD MORE · {(total - products.length).toLocaleString()} MORE
                  </button>
                )}
                {!loadingMore && products.length >= total && total > PAGE_SIZE && (
                  <p
                    data-testid="silo-end-of-list"
                    className="font-label text-[10px] tracking-[0.28em] text-ink/40"
                  >
                    THAT'S ALL {total.toLocaleString()} LOOKS · YOU'VE REACHED THE END
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
